import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Plus, Phone, PencilSimple, X, Trash, FloppyDisk, UserCircle, Ticket,
  Camera, Wrench, CalendarCheck, CurrencyDollar, Eye, ChatText, CheckCircle,
  ArrowLeft, Envelope, PhoneCall, NotePencil, ClipboardText, CalendarDots,
  WhatsappLogo, CaretDown, CaretRight, Clock, MapPin, User, Buildings,
  ShieldCheck, UploadSimple, FileText, Handshake
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { ComercialStatusBadge } from "./ComercialStatusBadge";

export const ComercialDetail = ({ contactId, onBack, statusTypes, onCreateQuotation, onViewQuotation }) => {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";
  const canEdit = ["admin", "comercial", "auxiliar", "ingeniero", "subgerente"].includes(user?.role);
  const canDelete = user?.role === "admin";

  const [contact, setContact] = useState(null);
  const [history, setHistory] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [followUpComments, setFollowUpComments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [companyTickets, setCompanyTickets] = useState([]);
  const [companyQuotations, setCompanyQuotations] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [interactionTypes, setInteractionTypes] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [policyTypes, setPolicyTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState("activity");
  const [activityFilter, setActivityFilter] = useState("all");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPersonForm, setShowPersonForm] = useState(false);
  const [personForm, setPersonForm] = useState({ name: "", position: "", phone: "", whatsapp: "", email: "" });
  const [editingPersonId, setEditingPersonId] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [newInteraction, setNewInteraction] = useState({ type: "llamada", notes: "", date: new Date().toISOString().slice(0, 10) });
  const [showEquipmentForm, setShowEquipmentForm] = useState(false);
  const [eqForm, setEqForm] = useState({ serial_number: "", description: "", installation_date: "", implementation_date: "" });
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [cRes, hRes, iRes, fcRes, rRes, eqRes, itRes, sRes, ptRes] = await Promise.all([
        axios.get(`${API}/comercial/${contactId}`, { headers }),
        axios.get(`${API}/comercial/${contactId}/history`, { headers }),
        axios.get(`${API}/comercial/${contactId}/interactions`, { headers }),
        axios.get(`${API}/comercial/${contactId}/comments`, { headers }),
        axios.get(`${API}/comercial/${contactId}/reminders`, { headers }),
        axios.get(`${API}/comercial/${contactId}/equipment`, { headers }),
        axios.get(`${API}/comercial/interaction-types`, { headers }),
        axios.get(`${API}/comercial/sellers`, { headers }),
        axios.get(`${API}/admin/policy-types`, { headers }).catch(() => ({ data: [] })),
      ]);
      setContact(cRes.data);
      setHistory(hRes.data || []);
      setInteractions(iRes.data || []);
      setFollowUpComments(fcRes.data || []);
      setReminders(rRes.data || []);
      setEquipment(eqRes.data || []);
      setInteractionTypes(itRes.data || []);
      setSellers(sRes.data || []);
      setPolicyTypes(ptRes.data || []);

      if (cRes.data?.company_name) {
        const [tRes, qRes] = await Promise.all([
          axios.get(`${API}/comercial/${contactId}/tickets`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/comercial/${contactId}/quotations`, { headers }).catch(() => ({ data: [] })),
        ]);
        setCompanyTickets(tRes.data || []);
        setCompanyQuotations(qRes.data || []);
      }
    } catch (e) { toast.error("Error al cargar"); }
    setIsLoading(false);
  }, [contactId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (isLoading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;
  if (!contact) return <div className="text-center py-20 text-slate-400">Contacto no encontrado</div>;

  const c = contact;
  const contacts = c.contacts || [];
  const initials = (c.company_name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  // Handlers
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/comercial/${contactId}`, editForm, { headers });
      toast.success("Actualizado");
      setEditing(false);
      fetchData();
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  const handleAddPerson = async () => {
    if (!personForm.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      if (editingPersonId) {
        await axios.put(`${API}/comercial/${contactId}/persons/${editingPersonId}`, personForm, { headers });
        toast.success("Contacto actualizado");
      } else {
        await axios.post(`${API}/comercial/${contactId}/persons`, personForm, { headers });
        toast.success("Contacto agregado");
      }
      setPersonForm({ name: "", position: "", phone: "", whatsapp: "", email: "" });
      setShowPersonForm(false);
      setEditingPersonId(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleEditPerson = (p) => {
    setEditingPersonId(p.id);
    setPersonForm({ name: p.name, position: p.position || "", phone: p.phone || "", whatsapp: p.whatsapp || "", email: p.email || "" });
    setShowPersonForm(true);
  };

  const handleDeletePerson = async (personId) => {
    if (!window.confirm("Eliminar este contacto?")) return;
    try { await axios.delete(`${API}/comercial/${contactId}/persons/${personId}`, { headers }); fetchData(); } catch { toast.error("Error"); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/comercial/${contactId}/comments`, { comment: newNote }, { headers });
      setNewNote("");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleAddInteraction = async () => {
    try {
      const itType = interactionTypes.find(t => t.name === newInteraction.type);
      await axios.post(`${API}/comercial/${contactId}/interactions`, {
        ...newInteraction, type_name: itType?.label || newInteraction.type
      }, { headers });
      toast.success("Interaccion registrada");
      setShowInteractionForm(false);
      setNewInteraction({ type: "llamada", notes: "", date: new Date().toISOString().slice(0, 10) });
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/comercial/${contactId}`, { status: newStatus }, { headers });
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleUploadEvidence = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/comercial/${contactId}/evidence`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Evidencia subida");
      fetchData();
    } catch { toast.error("Error"); }
    setUploading(false);
    e.target.value = "";
  };

  const handleAddEquipment = async () => {
    if (!eqForm.serial_number.trim()) { toast.error("Numero de serie requerido"); return; }
    try {
      await axios.post(`${API}/comercial/${contactId}/equipment`, eqForm, { headers });
      toast.success("Equipo agregado");
      setShowEquipmentForm(false);
      setEqForm({ serial_number: "", description: "", installation_date: "", implementation_date: "" });
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleAssignSeller = async (sellerId) => {
    const seller = sellers.find(s => s.id === sellerId);
    if (!seller) return;
    try {
      await axios.put(`${API}/comercial/${contactId}`, { assigned_to_id: sellerId, assigned_to_name: seller.name }, { headers });
      fetchData();
    } catch { toast.error("Error"); }
  };

  // Build unified timeline
  const buildTimeline = () => {
    const items = [];

    history.forEach(h => items.push({ ...h, _type: "history", _date: h.created_at }));
    interactions.forEach(i => items.push({ ...i, _type: "interaction", _date: i.date || i.created_at }));
    followUpComments.forEach(f => items.push({ ...f, _type: "followup", _date: f.created_at }));
    companyTickets.forEach(t => items.push({ ...t, _type: "ticket", _date: t.created_at }));

    items.sort((a, b) => new Date(b._date) - new Date(a._date));

    // Filter
    if (activityFilter === "calls") return items.filter(i => i._type === "interaction" && (i.type === "llamada" || i.type === "call"));
    if (activityFilter === "visits") return items.filter(i => i._type === "interaction" && (i.type === "visita" || i.type === "visit"));
    if (activityFilter === "notes") return items.filter(i => i._type === "followup");
    if (activityFilter === "tickets") return items.filter(i => i._type === "ticket");
    return items;
  };

  const timeline = buildTimeline();

  // Group by month
  const groupedTimeline = {};
  timeline.forEach(item => {
    const d = new Date(item._date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = format(d, "MMMM yyyy", { locale: es });
    if (!groupedTimeline[key]) groupedTimeline[key] = { label, items: [] };
    groupedTimeline[key].items.push(item);
  });

  const getTimelineIcon = (item) => {
    if (item._type === "ticket") return { icon: <Ticket size={14} />, cls: "bg-teal-100 text-teal-600" };
    if (item._type === "interaction") {
      if (item.type === "llamada" || item.type === "call") return { icon: <PhoneCall size={14} />, cls: "bg-amber-100 text-amber-600" };
      if (item.type === "visita" || item.type === "visit") return { icon: <MapPin size={14} />, cls: "bg-green-100 text-green-600" };
      if (item.type === "email") return { icon: <Envelope size={14} />, cls: "bg-blue-100 text-blue-600" };
      return { icon: <ChatText size={14} />, cls: "bg-indigo-100 text-indigo-600" };
    }
    if (item._type === "followup") return { icon: <NotePencil size={14} />, cls: "bg-violet-100 text-violet-600" };
    if (item.action === "status_changed") return { icon: <Clock size={14} />, cls: "bg-slate-100 text-slate-500" };
    return { icon: <ClipboardText size={14} />, cls: "bg-slate-100 text-slate-500" };
  };

  const getTimelineTitle = (item) => {
    if (item._type === "ticket") return <><span className="text-teal-600 font-bold">Ticket #{item.ticket_number}</span> — {item.description?.slice(0, 80)}</>;
    if (item._type === "interaction") return <><span className="text-blue-600">{item.type_name || item.type}</span> {item.notes ? `— ${item.notes.slice(0, 100)}` : ""}</>;
    if (item._type === "followup") return <>Nota de seguimiento — {item.comment?.slice(0, 100)}</>;
    return <>{item.details}</>;
  };

  const fmtDate = (d) => { try { return format(new Date(d), "dd MMM yyyy, h:mm a", { locale: es }); } catch { return d; } };

  return (
    <div className="h-full flex flex-col" data-testid="comercial-detail">
      {/* Top Bar */}
      <div className="bg-white border-b px-4 py-2.5 flex items-center gap-3 shrink-0">
        <button onClick={onBack} className="text-blue-600 text-sm font-semibold flex items-center gap-1 hover:underline" data-testid="comercial-back"><ArrowLeft size={16} /> Contactos</button>
        <span className="text-slate-300">|</span>
        <span className="text-sm font-bold text-slate-800">{c.company_name}</span>
        <div className="ml-auto flex gap-2">
          {canEdit && (
            <Select value={c.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{statusTypes.map(s => <SelectItem key={s.id || s.name} value={s.name}><span className="text-xs">{s.label}</span></SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[280px_1fr_280px] min-h-0 overflow-hidden">

        {/* ===== LEFT SIDEBAR ===== */}
        <div className="bg-white border-r overflow-y-auto p-5 space-y-4">
          {/* Company Header */}
          <div className="text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-xl font-extrabold mx-auto mb-3">{initials}</div>
            {editing ? (
              <div className="space-y-2 text-left">
                <div><Label className="text-[10px]">Empresa</Label><Input value={editForm.company_name || ""} onChange={e => setEditForm({...editForm, company_name: e.target.value})} className="h-8 text-xs" placeholder="Nombre de empresa" data-testid="edit-company-name" /></div>
                <div><Label className="text-[10px]">Celular</Label><Input value={editForm.phone || ""} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">WhatsApp</Label><Input value={editForm.whatsapp || ""} onChange={e => setEditForm({...editForm, whatsapp: e.target.value})} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">Correo</Label><Input value={editForm.email || ""} onChange={e => setEditForm({...editForm, email: e.target.value})} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">Ciudad</Label><Input value={editForm.city || ""} onChange={e => setEditForm({...editForm, city: e.target.value})} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">Estado</Label><Input value={editForm.state || ""} onChange={e => setEditForm({...editForm, state: e.target.value})} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">Notas</Label><Textarea value={editForm.sales_comments || ""} onChange={e => setEditForm({...editForm, sales_comments: e.target.value})} className="text-xs min-h-[60px]" /></div>
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 text-xs flex-1" onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn"><FloppyDisk size={12} className="mr-1" /> {saving ? "Guardando..." : "Guardar"}</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(false)}><X size={12} /></Button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-extrabold text-slate-900 leading-tight">{c.company_name || <span className="text-slate-400 italic">Sin empresa</span>}</h2>
                <div className="mt-2"><ComercialStatusBadge status={c.status} statusTypes={statusTypes} /></div>
                {canEdit && (
                  <button onClick={() => { setEditing(true); setEditForm({ company_name: c.company_name || "", phone: c.phone || "", whatsapp: c.whatsapp || "", email: c.email || "", city: c.city || "", state: c.state || "", sales_comments: c.sales_comments || "" }); }}
                    className="mt-2 text-[10px] text-blue-600 font-semibold hover:underline flex items-center gap-1 mx-auto" data-testid="edit-company-btn">
                    <PencilSimple size={11} /> Editar datos
                  </button>
                )}
              </>
            )}
          </div>

          {/* Action Icons */}
          <div className="flex justify-center gap-1.5 pb-4 border-b">
            {[
              { icon: <NotePencil size={15} />, label: "Nota", action: () => document.getElementById("note-input")?.focus() },
              { icon: <Envelope size={15} />, label: "Email", action: () => {} },
              { icon: <PhoneCall size={15} />, label: "Llamar", action: () => { setShowInteractionForm(true); setNewInteraction({ ...newInteraction, type: "llamada" }); } },
              { icon: <ChatText size={15} />, label: "Log", action: () => setShowInteractionForm(true) },
              { icon: <CheckCircle size={15} />, label: "Tarea", action: () => {} },
              { icon: <CalendarDots size={15} />, label: "Cita", action: () => { setShowInteractionForm(true); setNewInteraction({ ...newInteraction, type: "visita" }); } },
            ].map((a, i) => (
              <button key={i} onClick={a.action} className="w-10 h-10 rounded-xl border border-slate-200 flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title={a.label}>
                {a.icon}
                <span className="text-[8px] font-semibold">{a.label}</span>
              </button>
            ))}
          </div>

          {/* Contacts List */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Contactos ({contacts.length})</p>
              {canEdit && <button onClick={() => { setShowPersonForm(true); setEditingPersonId(null); setPersonForm({ name: "", position: "", phone: "", whatsapp: "", email: "" }); }} className="text-[10px] font-semibold text-blue-600" data-testid="add-person-btn">+ Agregar</button>}
            </div>

            {showPersonForm && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2 mb-3" data-testid="person-form">
                <Input value={personForm.name} onChange={e => setPersonForm({...personForm, name: e.target.value})} placeholder="Nombre *" className="h-8 text-xs" />
                <Input value={personForm.position} onChange={e => setPersonForm({...personForm, position: e.target.value})} placeholder="Puesto" className="h-8 text-xs" />
                <Input value={personForm.phone} onChange={e => setPersonForm({...personForm, phone: e.target.value})} placeholder="Celular" className="h-8 text-xs" />
                <Input value={personForm.whatsapp} onChange={e => setPersonForm({...personForm, whatsapp: e.target.value})} placeholder="WhatsApp" className="h-8 text-xs" />
                <Input value={personForm.email} onChange={e => setPersonForm({...personForm, email: e.target.value})} placeholder="Correo" className="h-8 text-xs" />
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 text-xs" onClick={handleAddPerson}>{editingPersonId ? "Guardar" : "Agregar"}</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setShowPersonForm(false); setEditingPersonId(null); }}>Cancelar</Button>
                </div>
              </div>
            )}

            {contacts.map(p => (
              <div key={p.id} className="bg-slate-50 border rounded-xl p-3 mb-2 hover:border-blue-300 transition-colors group" data-testid={`person-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-800">{p.name}</p>
                    {p.position && <p className="text-[10px] text-slate-500">{p.position}</p>}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canEdit && <button onClick={() => handleEditPerson(p)} className="text-slate-400 hover:text-blue-600"><PencilSimple size={12} /></button>}
                    {canDelete && <button onClick={() => handleDeletePerson(p.id)} className="text-slate-400 hover:text-red-500"><Trash size={12} /></button>}
                  </div>
                </div>
                <div className="mt-1.5 space-y-1">
                  {p.phone && <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><Phone size={10} className="text-slate-400" /> {p.phone}</div>}
                  {p.whatsapp && <div className="flex items-center gap-1.5 text-[10px]"><WhatsappLogo size={10} weight="fill" className="text-green-500" /> <a href={`https://wa.me/${p.whatsapp.replace(/\D/g,"")}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">{p.whatsapp}</a></div>}
                  {p.email && <div className="flex items-center gap-1.5 text-[10px]"><Envelope size={10} className="text-blue-400" /> <a href={`mailto:${p.email}`} className="text-blue-600 hover:underline">{p.email}</a></div>}
                </div>
              </div>
            ))}
            {contacts.length === 0 && <p className="text-xs text-slate-400 text-center py-3">Sin contactos</p>}
          </div>

          {/* General Info */}
          <div className="border-t pt-3 space-y-2.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Informacion General</p>
            {[
              { label: "Ciudad / Estado", value: [c.city, c.state].filter(Boolean).join(", ") || "—" },
              { label: "Asignado a", value: c.assigned_to_name || "—" },
              { label: "Creado por", value: c.created_by_name || "—" },
              { label: "Fecha creacion", value: c.created_at ? format(new Date(c.created_at), "dd MMM yyyy", { locale: es }) : "—" },
              { label: "Poliza", value: c.policy_type_name || "Sin poliza", cls: c.policy_type_name ? "text-emerald-600 font-bold" : "text-slate-400" },
            ].map((f, i) => (
              <div key={i}>
                <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">{f.label}</p>
                <p className={`text-xs font-medium ${f.cls || "text-slate-700"}`}>{f.value}</p>
              </div>
            ))}

            {/* Assign seller */}
            {canEdit && (
              <div>
                <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Asignar vendedor</p>
                <Select value={c.assigned_to_id || ""} onValueChange={handleAssignSeller}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{sellers.map(s => <SelectItem key={s.id} value={s.id}><span className="text-xs">{s.name}</span></SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* ===== CENTER PANEL ===== */}
        <div className="bg-white flex flex-col min-h-0 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b px-5 shrink-0">
            {[
              { id: "activity", label: "Actividad" },
              { id: "notes", label: "Notas" },
              { id: "interactions", label: "Interacciones" },
              { id: "negociaciones", label: "Negociaciones" },
              { id: "quotations", label: "Cotizaciones" },
              { id: "tickets", label: "Tickets" },
            ].map(t => (
              <button key={t.id} onClick={() => { setActiveTab(t.id); setActivityFilter(t.id === "notes" ? "notes" : t.id === "tickets" ? "tickets" : "all"); }}
                className={`px-4 py-3 text-xs font-semibold border-b-2 transition-colors ${activeTab === t.id ? "text-blue-600 border-blue-600" : "text-slate-400 border-transparent hover:text-slate-600"}`}
                data-testid={`tab-${t.id}`}>{t.label}</button>
            ))}
          </div>

          {/* Filter pills */}
          {activeTab === "activity" && (
            <div className="flex gap-2 px-5 py-3 border-b shrink-0">
              {[{ id: "all", l: "Todas" }, { id: "calls", l: "Llamadas" }, { id: "visits", l: "Visitas" }, { id: "notes", l: "Notas" }, { id: "tickets", l: "Tickets" }].map(f => (
                <button key={f.id} onClick={() => setActivityFilter(f.id)}
                  className={`px-3 py-1 rounded-full text-[10px] font-semibold border transition-colors ${activityFilter === f.id ? "bg-blue-50 border-blue-300 text-blue-600" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{f.l}</button>
              ))}
              <span className="ml-auto text-[10px] text-slate-400 self-center">{timeline.length} actividades</span>
            </div>
          )}

          {/* Timeline */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">
            {activeTab === "negociaciones" ? (
              <ContactNegociaciones contactId={contactId} contact={c} token={token} user={user} />
            ) : activeTab === "quotations" ? (
              <div className="space-y-2">
                {companyQuotations.length === 0 ? <p className="text-center text-sm text-slate-400 py-8">Sin cotizaciones</p> :
                  companyQuotations.map(q => (
                    <div key={q.id} onClick={() => onViewQuotation?.(q.id)} className="flex items-center justify-between p-3 bg-slate-50 border rounded-xl cursor-pointer hover:border-blue-300 transition-colors">
                      <div>
                        <p className="text-sm font-bold text-slate-800">{q.quotation_number}</p>
                        <p className="text-xs text-slate-500">${Number(q.total_mxn || 0).toLocaleString("es-MX")} MXN · {q.created_at ? format(new Date(q.created_at), "dd MMM yyyy", { locale: es }) : ""}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${q.status === "approved" ? "bg-emerald-50 text-emerald-700" : q.status === "sent" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>{q.status === "approved" ? "Aprobada" : q.status === "sent" ? "Enviada" : "Borrador"}</span>
                    </div>
                  ))
                }
              </div>
            ) : activeTab === "tickets" ? (
              <div className="space-y-2">
                {companyTickets.length === 0 ? <p className="text-center text-sm text-slate-400 py-8">Sin tickets</p> :
                  companyTickets.map(t => (
                    <div key={t.id} className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-200 rounded-xl">
                      <Ticket size={18} className="text-teal-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-800">{t.ticket_number}</p>
                        <p className="text-xs text-slate-500 truncate">{t.description}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${t.status === "resuelto" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>{t.status_label || t.status}</span>
                    </div>
                  ))
                }
              </div>
            ) : (
              Object.entries(groupedTimeline).map(([key, group]) => (
                <div key={key}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 py-2 border-b mb-3 capitalize">{group.label}</p>
                  {group.items.map((item, idx) => {
                    const { icon, cls } = getTimelineIcon(item);
                    return (
                      <div key={`${item._type}-${item.id}-${idx}`} className="flex gap-3 mb-4">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cls}`}>{icon}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800">{getTimelineTitle(item)}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{fmtDate(item._date)} {item.user_name ? `· ${item.user_name}` : ""}</p>
                          {item._type === "followup" && item.comment && <p className="text-xs text-slate-600 mt-1 bg-slate-50 rounded-lg p-2">{item.comment}</p>}
                          {item._type === "interaction" && item.notes && <p className="text-xs text-slate-600 mt-1 bg-slate-50 rounded-lg p-2">{item.notes}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
            {(activeTab === "activity" || activeTab === "notes" || activeTab === "interactions") && timeline.length === 0 && (
              <p className="text-center text-sm text-slate-400 py-8">Sin actividades</p>
            )}
          </div>

          {/* Bottom: New note / interaction */}
          <div className="shrink-0 border-t bg-slate-50 p-4">
            {showInteractionForm ? (
              <div className="space-y-2" data-testid="interaction-form">
                <div className="flex gap-2">
                  <Select value={newInteraction.type} onValueChange={v => setNewInteraction({...newInteraction, type: v})}>
                    <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>{interactionTypes.map(it => <SelectItem key={it.name} value={it.name}><span className="text-xs">{it.label || it.name}</span></SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="date" value={newInteraction.date} onChange={e => setNewInteraction({...newInteraction, date: e.target.value})} className="h-8 text-xs w-36" />
                  <Button size="sm" className="h-8 text-xs" onClick={handleAddInteraction}>Guardar</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowInteractionForm(false)}>Cancelar</Button>
                </div>
                <Textarea value={newInteraction.notes} onChange={e => setNewInteraction({...newInteraction, notes: e.target.value})} placeholder="Notas de la interaccion..." className="text-xs min-h-[60px]" />
              </div>
            ) : (
              <div className="flex gap-2">
                <Textarea id="note-input" value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Escribe una nota de seguimiento..." className="text-xs min-h-[40px] flex-1" />
                <div className="flex flex-col gap-1">
                  <Button size="sm" className="h-8 text-xs" onClick={handleAddNote} disabled={!newNote.trim()}>Nota</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowInteractionForm(true)}>+ Log</Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ===== RIGHT SIDEBAR ===== */}
        <div className="bg-white border-l overflow-y-auto">
          {/* Negociaciones */}
          <SidebarNegociaciones contactId={contactId} token={token} />

          {/* Quotations */}
          <SidebarSection title="Cotizaciones" count={companyQuotations.length} icon={<CurrencyDollar size={14} />} addLabel="+ Crear" onAdd={() => onCreateQuotation?.(c)}>
            {companyQuotations.slice(0, 3).map(q => (
              <div key={q.id} onClick={() => onViewQuotation?.(q.id)} className="flex items-center justify-between p-2.5 bg-slate-50 border rounded-lg mb-1.5 cursor-pointer hover:border-blue-300 text-xs transition-colors">
                <div><p className="font-bold text-slate-800">{q.quotation_number}</p><p className="text-[10px] text-slate-400">${Number(q.total_mxn || 0).toLocaleString("es-MX")} MXN</p></div>
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${q.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{q.status === "approved" ? "Aprobada" : "Pendiente"}</span>
              </div>
            ))}
          </SidebarSection>

          {/* Equipment */}
          <SidebarSection title="Equipos Instalados" count={equipment.length} icon={<Wrench size={14} />} addLabel="+ Agregar" onAdd={() => setShowEquipmentForm(!showEquipmentForm)}>
            {showEquipmentForm && (
              <div className="space-y-1.5 mb-2 bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                <Input value={eqForm.serial_number} onChange={e => setEqForm({...eqForm, serial_number: e.target.value})} placeholder="No. Serie *" className="h-7 text-xs" />
                <Input value={eqForm.description} onChange={e => setEqForm({...eqForm, description: e.target.value})} placeholder="Descripcion" className="h-7 text-xs" />
                <Input type="date" value={eqForm.installation_date} onChange={e => setEqForm({...eqForm, installation_date: e.target.value})} className="h-7 text-xs" />
                <Button size="sm" className="h-7 text-xs w-full" onClick={handleAddEquipment}>Agregar</Button>
              </div>
            )}
            {equipment.map(eq => (
              <div key={eq.id} className="p-2.5 bg-slate-50 border rounded-lg mb-1.5 text-xs">
                <p className="font-bold text-slate-800">{eq.serial_number}</p>
                <p className="text-[10px] text-slate-400">{eq.description} {eq.installation_date ? `· Inst: ${eq.installation_date}` : ""}</p>
              </div>
            ))}
          </SidebarSection>

          {/* Evidence */}
          <SidebarSection title="Evidencias" count={(c.evidence_images || []).length} icon={<Camera size={14} />}
            addLabel="+ Subir" onAdd={() => document.getElementById("evidence-upload")?.click()}>
            <input type="file" id="evidence-upload" className="hidden" onChange={handleUploadEvidence} />
            {(c.evidence_images || []).map((img, i) => (
              <div key={i} className="p-2 bg-slate-50 border rounded-lg mb-1.5 text-xs flex items-center gap-2">
                <FileText size={14} className="text-blue-500 shrink-0" />
                <p className="truncate text-slate-600">{typeof img === "string" ? img.split("/").pop() : "archivo"}</p>
              </div>
            ))}
          </SidebarSection>

          {/* Policy */}
          <SidebarSection title="Poliza" count={c.policy_type_name ? 1 : 0} icon={<ShieldCheck size={14} />} addLabel="Editar">
            {c.policy_type_name ? (
              <div className="p-2.5 bg-slate-50 border rounded-lg text-xs flex items-center justify-between">
                <div><p className="font-bold text-slate-800">{c.policy_type_name}</p><p className="text-[10px] text-slate-400">Activada: {c.policy_activation_date || "—"}</p></div>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Activa</span>
              </div>
            ) : <p className="text-[10px] text-slate-400 text-center py-2">Sin poliza asignada</p>}
          </SidebarSection>

          {/* Tickets */}
          <SidebarSection title="Tickets" count={companyTickets.length} icon={<Ticket size={14} className="text-teal-600" />} addLabel="Ver todos" onAdd={() => setActiveTab("tickets")}>
            {companyTickets.slice(0, 2).map(t => (
              <div key={t.id} className="p-2.5 bg-teal-50 border border-teal-200 rounded-lg mb-1.5 text-xs">
                <p className="font-bold text-teal-800">{t.ticket_number}</p>
                <p className="text-[10px] text-teal-600 truncate">{t.description}</p>
              </div>
            ))}
          </SidebarSection>

          {/* Reminders */}
          <SidebarSection title="Recordatorios" count={reminders.length} icon={<Clock size={14} />} addLabel="+ Crear">
            {reminders.slice(0, 3).map(r => (
              <div key={r.id} className="p-2.5 bg-slate-50 border rounded-lg mb-1.5 text-xs flex items-center justify-between">
                <div><p className="font-bold text-slate-800">{r.title}</p><p className="text-[10px] text-slate-400">{r.remind_date ? format(new Date(r.remind_date), "dd MMM yyyy", { locale: es }) : ""}</p></div>
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${r.completed ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{r.completed ? "Hecho" : "Pendiente"}</span>
              </div>
            ))}
          </SidebarSection>
        </div>
      </div>
    </div>
  );
};

// Inline negociaciones list for a contact
function ContactNegociaciones({ contactId, contact, token, user }) {
  const [negs, setNegs] = useState([]);
  const [stages, setStages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", amount: "", notes: "", stage: "en_desarrollo" });
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/negociaciones?contact_id=${contactId}`, { headers }).then(r => setNegs(r.data)).catch(() => {});
    axios.get(`${API}/negociaciones/stages`, { headers }).then(r => setStages(r.data)).catch(() => {});
  }, [contactId]);

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/negociaciones`, {
        ...form,
        amount: parseFloat(form.amount) || 0,
        contact_id: contactId,
        company_name: contact?.company_name || "",
        contact_name: contact?.contact_name || "",
      }, { headers });
      setNegs(prev => [res.data, ...prev]);
      setShowForm(false);
      setForm({ title: "", amount: "", notes: "", stage: "en_desarrollo" });
      toast.success("Negociacion creada");
    } catch { toast.error("Error al crear negociacion"); }
  };

  const stageMap = Object.fromEntries(stages.map(s => [s.id, s]));
  const totalAmount = negs.reduce((sum, n) => sum + (n.amount || 0), 0);

  return (
    <div className="space-y-3" data-testid="contact-negociaciones">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Handshake size={16} className="text-blue-600" /> Negociaciones ({negs.length})
          {totalAmount > 0 && <span className="text-xs font-normal text-emerald-600">${totalAmount.toLocaleString("es-MX")}</span>}
        </p>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowForm(!showForm)} data-testid="new-neg-contact-btn">
          <Plus size={12} className="mr-1" /> Nueva
        </Button>
      </div>

      {showForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
          <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Titulo (opcional)" className="h-8 text-xs" data-testid="neg-title-input" />
          <Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="Monto ($)" className="h-8 text-xs" data-testid="neg-amount-input" />
          <Select value={form.stage} onValueChange={v => setForm({...form, stage: v})}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}><span className="text-xs">{s.name}</span></SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Notas..." className="text-xs min-h-[50px]" />
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs flex-1" onClick={handleCreate}>Crear</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowForm(false)}>Cancelar</Button>
          </div>
        </div>
      )}

      {negs.length === 0 && !showForm && <p className="text-center text-sm text-slate-400 py-8">Sin negociaciones</p>}
      {negs.map(n => {
        const stage = stageMap[n.stage] || {};
        return (
          <div key={n.id} className="p-3 bg-slate-50 border rounded-xl hover:border-blue-300 transition-colors" data-testid={`neg-card-${n.id}`}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-bold text-slate-800">{n.number}</p>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: (stage.color || "#64748b") + "20", color: stage.color || "#64748b" }}>{stage.name || n.stage}</span>
            </div>
            {n.title && <p className="text-xs text-slate-600 mb-1">{n.title}</p>}
            <div className="flex items-center justify-between">
              <p className="text-sm font-extrabold text-emerald-700">${(n.amount || 0).toLocaleString("es-MX")}</p>
              <p className="text-[10px] text-slate-400">{n.created_at ? format(new Date(n.created_at), "dd MMM yyyy", { locale: es }) : ""}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// Sidebar negociaciones with inline detail slide-over
function SidebarNegociaciones({ contactId, token }) {
  const [negs, setNegs] = useState([]);
  const [stages, setStages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/negociaciones?contact_id=${contactId}`, { headers }).then(r => setNegs(r.data)).catch(() => {});
    axios.get(`${API}/negociaciones/stages`, { headers }).then(r => setStages(r.data)).catch(() => {});
  }, [contactId]);

  const stageMap = Object.fromEntries(stages.map(s => [s.id, s]));
  const total = negs.reduce((s, n) => s + (n.amount || 0), 0);

  const openNeg = (neg) => {
    setSelected(neg);
    axios.get(`${API}/negociaciones/${neg.id}/history`, { headers }).then(r => setHistory(r.data)).catch(() => setHistory([]));
  };

  return (
    <>
      <div className="border-b">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Handshake size={14} className="text-blue-600" />
            <span className="text-xs font-bold text-slate-800">Negociaciones</span>
            <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">{negs.length}</span>
          </div>
          {total > 0 && <span className="text-[10px] font-bold text-emerald-600">${total.toLocaleString("es-MX")}</span>}
        </div>
        <div className="px-4 pb-3 space-y-1.5">
          {negs.length === 0 && <p className="text-[10px] text-slate-400 text-center py-2">Sin negociaciones</p>}
          {negs.map(n => {
            const st = stageMap[n.stage] || {};
            return (
              <div key={n.id} onClick={() => openNeg(n)} className="flex items-center justify-between p-2.5 rounded-lg border cursor-pointer hover:border-blue-300 transition-colors" style={{ borderLeftWidth: 3, borderLeftColor: st.color || "#64748b" }} data-testid={`sidebar-neg-${n.id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-800">{n.number}</p>
                  <p className="text-[10px] text-slate-500 truncate">{n.title || "—"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-extrabold text-slate-900">${(n.amount || 0).toLocaleString("es-MX")}</p>
                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: (st.color || "#64748b") + "18", color: st.color }}>{st.name || n.stage}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Inline Neg Detail Slide-over */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="sidebar-neg-detail">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-sm bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
              <div>
                <p className="text-[10px] font-bold text-slate-400">{selected.number}</p>
                <p className="text-sm font-bold text-slate-900">{selected.title || selected.number}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="text-center py-3 bg-slate-50 rounded-xl">
                <p className="text-2xl font-extrabold text-slate-900">${(selected.amount || 0).toLocaleString("es-MX")}</p>
              </div>
              <div className="flex gap-2">
                {(() => { const s = stageMap[selected.stage]; return s ? <span className="text-xs font-bold px-3 py-1 rounded-lg" style={{ backgroundColor: s.color + "15", color: s.color }}>{s.name}</span> : null; })()}
                {selected.payment_status && <span className={`text-xs font-bold px-3 py-1 rounded-lg ${selected.payment_status === "pagado" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{selected.payment_status === "pagado" ? "Pagado" : "No Pagado"}</span>}
              </div>
              {selected.next_contact_date && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs flex items-center gap-2">
                  <CalendarDots size={12} className="text-amber-600" />
                  <span className="font-semibold text-amber-800">Prox. contacto: {format(new Date(selected.next_contact_date + "T12:00:00"), "dd MMM yyyy", { locale: es })}</span>
                </div>
              )}
              {selected.notes && <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 whitespace-pre-wrap">{selected.notes}</div>}

              {/* History */}
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1.5">Historial ({history.length})</p>
                {history.slice(0, 10).map((h, i) => (
                  <div key={h.id || i} className="flex gap-2 py-1.5 border-b border-slate-100 last:border-0 text-xs">
                    <div className="w-1 rounded-full shrink-0" style={{ backgroundColor: h.action === "stage_changed" ? "#8B5CF6" : h.action === "amount_changed" ? "#10B981" : "#94a3b8" }} />
                    <div><p className="text-slate-700">{h.details}</p><p className="text-[10px] text-slate-400">{h.user_name} · {h.created_at?.slice(0, 16).replace("T", " ")}</p></div>
                  </div>
                ))}
                {history.length === 0 && <p className="text-[10px] text-slate-400 text-center py-2">Sin historial</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// Reusable sidebar section component
function SidebarSection({ title, count, icon, addLabel, onAdd, children }) {
  return (
    <div className="border-b">
      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">{icon} {title} <span className="text-slate-400 font-normal">({count})</span></p>
        {addLabel && <button onClick={onAdd} className="text-[10px] font-semibold text-blue-600 hover:underline">{addLabel}</button>}
      </div>
      {children && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}
